import { LitElement, css } from 'lit';
import CustomConverter, { typeCastValue } from './lib/custom_converter.js';
import normalize from './normalize.lit.css';
import base from './base.lit.css';
import helpers from './helpers.lit.css';
import i18n from '../../lib/i18n.js';
import translations from './translations.js';
import '../../lib/set_up_ionic.js'; // automatically sets up ionic components

const isChromiumBrowser = () =>
  !!navigator.userAgentData &&
  navigator.userAgentData.brands.some(data => data.brand === 'Chromium');
const chromiumStyle = css`
  img {
    image-rendering: -webkit-optimize-contrast;
  }
`;
const styles = [normalize, base, helpers];

isChromiumBrowser() && styles.push(chromiumStyle);

export * from 'lit';

export class ApplicationElement extends LitElement {
  static styles = styles;

  /**
   * Used for configuration of other components e.g.:
   * `ApplicationElement.customConfig['ef-avatar'].disableInitials = true`
   */
  static customConfig = {};

  static i18n = i18n;

  /** @internal */
  static translations = translations;

  static properties = {
    AppConfig: { type: Object, state: true },
  };

  static extendTranslationsAs(tagName) {
    this.translationScope = `web_components.${tagName}`;
    this.translations ||= {};

    Object.entries(this.translations).forEach(([locale, translations]) => {
      // store is doing the deep merge of two translations => https://github.com/fnando/i18n#updating-translation-store
      this.i18n.store({
        [locale]: { web_components: { [tagName]: translations } },
      });
    });
  }

  static registerTagName(tagName) {
    this.extendTranslationsAs(tagName);
  }

  static createProperty(name, options) {
    super.createProperty(name, { converter: CustomConverter, ...options });
  }

  /**
   * Overrides getPropertyDescriptor to support type casting as for attribute
   *
   * https://lit.dev/docs/api/ReactiveElement/#ReactiveElement.getPropertyDescriptor
   *
   * @override
   */
  static getPropertyDescriptor(name, key, options) {
    const defaultDescriptor = super.getPropertyDescriptor(name, key, options);

    return {
      ...defaultDescriptor,
      set(value) {
        defaultDescriptor.set.call(this, typeCastValue(value, options.type));
      },
    };
  }

  constructor() {
    super();
    this.AppConfig = {};
    /** @internal */
    this.parentConstructors = this.collectParentConstructors();
  }

  /**
   * Turbolinks hack to clear content before re-render
   *
   * @inheritdoc
   */
  willUpdate() {
    super.willUpdate();
    if (!this.shadowRoot && !this.hasUpdated) {
      this.innerHTML = '';
    }
  }

  /** @internal */
  get i18n() {
    return this.constructor.i18n;
  }

  /** @internal */
  get customConfig() {
    return this.constructor.customConfig[this.tagName.toLowerCase()] || {};
  }

  /** @internal */
  get translationFallbackScopes() {
    this._translationFallbackScopes = this.parentConstructors.reduce(
      (scopes, parentConstructor) => {
        const parentScope = parentConstructor.translationScope;
        if (parentScope) scopes.push(parentScope);
        return scopes;
      },
      []
    );
    return this._translationFallbackScopes;
  }

  collectParentConstructors() {
    const parents = [];
    let parentConstructor = this.constructor;
    while (parentConstructor !== LitElement) {
      parentConstructor = Object.getPrototypeOf(parentConstructor);
      parents.push(parentConstructor);
    }

    return parents;
  }

  t(key, opts = {}) {
    const { scope, ...otherOpts } = opts;
    const { translationScope } = this.constructor;
    let finalKey = [translationScope, key];

    if (scope) {
      finalKey = [translationScope, scope, key];
    }

    const defaults = this.translationFallbackScopes.map(scope => ({
      scope: [scope, key],
    }));

    const configuration = { defaults, ...otherOpts };

    return this.i18n.t(finalKey, configuration);
  }

  activateByCurrentLink() {
    return this.active === undefined
      ? window.location.href.includes(this.href)
      : this.active;
  }
}

ApplicationElement.extendTranslationsAs('ef-application-element');
export default ApplicationElement;

// Hack so export translations script would work
// TODO: fix export translations script to use extendTranslationsAs
// customElements.get('ef-application-element');
